import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Award, Users, Target, Shield } from "lucide-react";

const MEETING_IMG = "https://images.unsplash.com/photo-1752659985859-c22670d79563?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjBzdHJhdGVneSUyMG1lZXRpbmclMjB3aGl0ZWJvYXJkJTIwZGl2ZXJzZSUyMHRlYW18ZW58MHx8fHwxNzcxMzcyNjk0fDA&ixlib=rb-4.1.0&q=85";

const CEO_IMG = "https://customer-assets.emergentagent.com/job_anglin-consult/artifacts/ygex7ivz_collin_anglin_ycla_2-removebg-preview.png";

const values = [
  {
    icon: Award,
    title: "Excellence",
    description: "We deliver championship-level quality in everything we do.",
  },
  {
    icon: Users,
    title: "Partnership",
    description: "Your success is our success. We work alongside you as true partners.",
  },
  {
    icon: Target,
    title: "Results-Driven",
    description: "Every strategy is designed with measurable outcomes in mind.",
  },
  {
    icon: Shield,
    title: "Integrity",
    description: "Honest, transparent, and ethical in all our business dealings.",
  },
];

const differentiators = [
  {
    number: "01",
    title: "Local Expertise, Global Standards",
    description:
      "Deep understanding of the Cayman Islands market combined with international best practices.",
  },
  {
    number: "02",
    title: "End-to-End Solutions",
    description:
      "From strategy to execution, we handle every aspect of your project.",
  },
  {
    number: "03",
    title: "Proven Track Record",
    description:
      "15+ years of successful projects, satisfied clients, and lasting partnerships.",
  },
  {
    number: "04",
    title: "Personalized Approach",
    description:
      "Every client receives tailored solutions designed for their unique needs and goals.",
  },
];

const AboutPage = () => {
  return (
    <main data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-[#020617] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#020617] to-[#172554] pointer-events-none" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        <div className="section-container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="label-accent mb-4 block"
              >
                About Anglin Consultants
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight text-white leading-[0.9] mb-6"
              >
                Building
                <br />
                <span className="text-[#E53935]">Champions</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-slate-400 text-lg leading-relaxed mb-8"
              >
                Anglin Sports Consultancy is a leading business and sports 
                consultancy based in the Cayman Islands. We specialize in 
                transforming organizations through strategic planning, 
                innovative event development, and powerful brand positioning.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Link
                  to="/contact"
                  data-testid="about-hero-cta"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Work With Us
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={MEETING_IMG}
                  alt="Strategic Meeting"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-full h-full border-2 border-[#3B4B8C] -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="section-padding bg-[#0F172A]">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* CEO Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="relative">
                <div className="aspect-[4/5] max-w-md mx-auto overflow-hidden">
                  <img
                    src={CEO_IMG}
                    alt="Collin Anglin, CEO"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Name Card */}
                <div className="absolute bottom-0 left-0 right-0 bg-[#020617]/90 backdrop-blur-sm p-6 max-w-md mx-auto">
                  <h3 className="font-heading text-2xl font-bold text-white">
                    Collin Anglin
                  </h3>
                  <p className="text-[#E53935] font-medium">
                    Founder & Chief Executive Officer
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="label-accent mb-4 block">Leadership</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
                Meet Our
                <br />
                <span className="text-[#E53935]">CEO</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6 text-lg">
                With over 20 years of experience in sports management and business 
                consulting, Collin Anglin has established himself as a trusted 
                advisor to organizations across the Caribbean. He was also the 
                Director of Sports for the Cayman Islands Government for 10 years 
                before retiring from that position.
              </p>
              <p className="text-slate-400 leading-relaxed mb-6">
                His expertise spans strategic planning, event management, sponsorship 
                development, and organizational governance. Collin has successfully 
                led numerous high-profile projects, from launching sports leagues to 
                securing major sponsorship deals.
              </p>
              <p className="text-slate-400 leading-relaxed">
                Passionate about elevating sports and business in the Cayman Islands, 
                Collin founded Anglin Sports Consultancy to bring world-class 
                consulting services to the region.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-[#020617]">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="card-service"
              data-testid="mission-card"
            >
              <span className="label-accent mb-4 block">Our Mission</span>
              <h3 className="font-heading text-3xl font-bold uppercase text-white mb-4">
                Empowering Success
              </h3>
              <p className="text-slate-400 leading-relaxed">
                To empower businesses and sports organizations with innovative 
                strategies, exceptional execution, and partnerships that deliver 
                sustainable growth and measurable impact.
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="card-service"
              data-testid="vision-card"
            >
              <span className="label-accent mb-4 block">Our Vision</span>
              <h3 className="font-heading text-3xl font-bold uppercase text-white mb-4">
                Caribbean Excellence
              </h3>
              <p className="text-slate-400 leading-relaxed">
                To be the Caribbean's most trusted consultancy, known for transforming 
                organizations and setting new standards in sports and business excellence.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#0F172A]">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="label-accent mb-4 block">What Guides Us</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`value-${value.title.toLowerCase()}`}
                className="text-center p-8 bg-[#020617] border border-[#1E293B] hover:border-[#3B4B8C] transition-colors"
              >
                <div className="w-16 h-16 rounded-full bg-[#E53935]/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#E53935]" />
                </div>
                <h3 className="font-heading text-xl font-bold uppercase text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-400 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-[#020617]">
        <div className="section-container">
          <div className="mb-16">
            <span className="label-accent mb-4 block">Our Advantage</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              Why Choose Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {differentiators.map((item, index) => (
              <motion.div
                key={item.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`differentiator-${item.number}`}
                className="flex gap-6 p-6 bg-[#0F172A] border border-[#1E293B] hover:border-[#E53935] transition-colors group"
              >
                <span className="font-heading text-5xl font-bold text-[#E53935]/30 group-hover:text-[#E53935]/50 transition-colors">
                  {item.number}
                </span>
                <div>
                  <h3 className="font-heading text-xl font-bold uppercase text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-400">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#E53935]/10 via-transparent to-[#3B4B8C]/10 pointer-events-none" />
        
        <div className="section-container relative z-10 text-center">
          <span className="label-accent mb-4 block">Join Our Journey</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
            Ready to Transform
            <br />
            <span className="text-[#E53935]">Your Organization?</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-10 text-lg">
            Let's discuss how Anglin Sports Consultancy can help you achieve 
            your goals and exceed expectations.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            Book a Consultation
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
